/**
 * The title block every screen opens with: what this is, and what you can do to
 * it. Consistent placement matters more than what it contains — the primary
 * action being in the same corner on every screen is most of what makes an admin
 * interface feel navigable.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface PageHeaderProps {
  title: ReactNode;
  /** One line under the title. What the screen is for, not how to use it. */
  description?: ReactNode;
  /** Buttons, right-aligned. They wrap under the title on a narrow viewport. */
  actions?: ReactNode;
  /** Breadcrumbs or tabs, rendered above the title. */
  above?: ReactNode;
}

export function PageHeader({ title, description, actions, above }: PageHeaderProps) {
  return (
    <Box sx={{ mb: 3 }}>
      {above && <Box sx={{ mb: 1 }}>{above}</Box>}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2} sx={{ alignItems: { xs: 'stretch', sm: 'flex-start' }, justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', flexShrink: 0 }}>
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
